import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Spin, DatePicker, Select, Divider, Tooltip } from 'antd';
import { 
  DollarOutlined, 
  UserOutlined, 
  RiseOutlined, 
  FallOutlined,
  TrophyOutlined,
  TeamOutlined,
  BellOutlined,
  PercentageOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const Metrics = () => {
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('month'); // day, week, month
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);
  
  // Descrições dos indicadores
  const tooltips = {
    mrr: 'Monthly Recurring Revenue - Receita recorrente mensal de clientes ativos com groupjid configurado',
    arr: 'Annual Recurring Revenue - MRR multiplicado por 12 meses',
    totalRevenue: 'Soma de todas as faturas pagas no período selecionado',
    trialConversion: 'Percentual de clientes que converteram de trial para premium nos últimos 90 dias',
    cac: 'Customer Acquisition Cost - Custo mensal (R$ 7.000) dividido por novos clientes',
    ltv: 'Lifetime Value - Tempo médio de vida do cliente multiplicado pelo ticket médio',
    ltvCacRatio: 'Relação entre LTV e CAC - Ideal acima de 3x',
    paybackPeriod: 'Tempo em meses para recuperar o investimento em aquisição',
    newCustomers: 'Total de novos clientes cadastrados no período',
    newActiveCustomers: 'Novos clientes que ativaram (configuraram groupjid) no período',
    activationRate: 'Percentual de cadastrados que se tornaram ativos (configuraram groupjid)',
    activeCustomers: 'Clientes com status ATIVO e groupjid configurado',
    growthRate: 'Taxa de crescimento de cadastros comparada ao período anterior',
    activeGrowthRate: 'Taxa de crescimento de clientes ativos comparada ao período anterior',
    churnRate: 'Percentual de clientes que cancelaram no período',
    churnMRR: 'Receita mensal perdida por cancelamentos',
    retentionRate: 'Percentual de clientes que permaneceram ativos (100 - churn)',
    reactivatedCustomers: 'Clientes que voltaram a ficar ativos após 30 dias inativos',
    defaultRate: 'Percentual de clientes ativos com pagamento pendente',
    pendingPayments: 'Total de clientes com status de pagamento PENDING',
    paidCustomers: 'Clientes ativos com pagamento em dia',
    overdueAmount: 'Valor total de faturas pendentes há mais de 30 dias',
    gamesCreated: 'Total de jogos criados no período',
    playersRegistered: 'Total de jogadores cadastrados no período',
    avgPlayersPerTeam: 'Média de jogadores ativos por time',
    avgGamesPerTeam: 'Média de jogos criados por time no período',
    confirmationRate: 'Percentual de jogadores que confirmaram presença',
    totalCalls: 'Estimativa de convocações enviadas (jogos x jogadores x 3)',
    callsPerTeam: 'Média de convocações por time ativo',
    responseRate: 'Taxa de resposta às convocações (igual à taxa de confirmação)',
    peakHours: 'Horário com maior concentração de jogos',
    momGrowth: 'Crescimento de cadastros comparado ao mês anterior',
    momActiveGrowth: 'Crescimento de ativos comparado ao mês anterior',
    wowGrowth: 'Crescimento de cadastros comparado à semana anterior',
    wowActiveGrowth: 'Crescimento de ativos comparado à semana anterior',
    dodGrowth: 'Crescimento de cadastros comparado ao dia anterior',
    dodActiveGrowth: 'Crescimento de ativos comparado ao dia anterior',
    seasonality: 'Dia da semana com mais cadastros nos últimos 90 dias',
    nrr: 'Net Revenue Retention - Receita retida + expansão (ideal > 100%)',
    quickRatio: 'Novos clientes / Churn - Indica saúde do crescimento (ideal > 4x)',
    burnRate: 'Diferença entre custos mensais e MRR',
    runway: 'Meses de operação com reserva de caixa atual',
    healthScore: 'Score de saúde do negócio baseado em 4 fatores (0-100)'
  };
  
  // Estados para cada métrica
  const [metrics, setMetrics] = useState({
    // Receita e Faturamento
    mrr: 0,
    arr: 0,
    totalRevenue: 0,
    trialConversion: 0,
    
    // Custos e Rentabilidade
    cac: 0,
    ltv: 0,
    ltvCacRatio: 0,
    paybackPeriod: 0,
    
    // Crescimento
    newCustomers: 0,
    activeCustomers: 0,
    growthRate: 0,
    
    // Retenção e Churn
    churnRate: 0,
    churnMRR: 0,
    retentionRate: 0,
    reactivatedCustomers: 0,
    
    // Pagamentos
    defaultRate: 0,
    pendingPayments: 0,
    paidCustomers: 0,
    overdueAmount: 0,
    
    // Engajamento
    gamesCreated: 0,
    playersRegistered: 0,
    avgPlayersPerTeam: 0,
    avgGamesPerTeam: 0,
    confirmationRate: 0,
    
    // Convocações
    totalCalls: 0,
    callsPerTeam: 0,
    responseRate: 0,
    peakHours: '',
    
    // Comparativos
    momGrowth: 0,
    wowGrowth: 0,
    dodGrowth: 0,
    seasonality: '',
    nrr: 0,
    quickRatio: 0,
    burnRate: 0,
    runway: 0,
    healthScore: 0
  });

  useEffect(() => {
    fetchMetrics();
  }, [period, dateRange]);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const response = await api.get('/metrics/dashboard', {
        params: {
          period,
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD')
        }
      });
      setMetrics(response.data);
    } catch (error) {
      console.error('Erro ao carregar métricas:', error);
    }
    setLoading(false);
  };

  const handlePeriodChange = (value) => {
    setPeriod(value);
    
    // Ajusta dateRange baseado no período
    if (value === 'day') {
      setDateRange([dayjs(), dayjs()]);
    } else if (value === 'week') {
      setDateRange([dayjs().startOf('week'), dayjs().endOf('week')]);
    } else if (value === 'month') {
      setDateRange([dayjs().startOf('month'), dayjs().endOf('month')]);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPercent = (value) => {
    return `${value.toFixed(2)}%`;
  };

  const StatTitle = ({ text, tooltip }) => (
    <span>
      {text}{' '}
      <Tooltip title={tooltip}>
        <InfoCircleOutlined style={{ color: '#1890ff', fontSize: 14, cursor: 'help' }} />
      </Tooltip>
    </span>
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Dashboard de Métricas</h1>
        <div style={{ display: 'flex', gap: 16 }}>
          <Select value={period} onChange={handlePeriodChange} style={{ width: 120 }}>
            <Option value="day">Diário</Option>
            <Option value="week">Semanal</Option>
            <Option value="month">Mensal</Option>
          </Select>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            format="DD/MM/YYYY"
          />
        </div>
      </div>

      {/* RECEITA E FATURAMENTO */}
      <Divider orientation="left">💰 Receita e Faturamento</Divider>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="MRR" tooltip={tooltips.mrr} />}
              value={metrics.mrr}
              precision={2}
              prefix={<DollarOutlined />}
              formatter={(value) => formatCurrency(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="ARR" tooltip={tooltips.arr} />}
              value={metrics.arr}
              precision={2}
              prefix={<DollarOutlined />}
              formatter={(value) => formatCurrency(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Receita Total" tooltip={tooltips.totalRevenue} />}
              value={metrics.totalRevenue}
              precision={2}
              prefix={<DollarOutlined />}
              formatter={(value) => formatCurrency(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Conversão Trial" tooltip={tooltips.trialConversion} />}
              value={metrics.trialConversion}
              precision={2}
              suffix="%"
              prefix={<PercentageOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* CUSTOS E RENTABILIDADE */}
      <Divider orientation="left">📊 Custos e Rentabilidade</Divider>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="CAC" tooltip={tooltips.cac} />}
              value={metrics.cac}
              precision={2}
              prefix={<DollarOutlined />}
              formatter={(value) => formatCurrency(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="LTV" tooltip={tooltips.ltv} />}
              value={metrics.ltv}
              precision={2}
              prefix={<DollarOutlined />}
              formatter={(value) => formatCurrency(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="LTV/CAC Ratio" tooltip={tooltips.ltvCacRatio} />}
              value={metrics.ltvCacRatio}
              precision={2}
              suffix="x"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Payback Period" tooltip={tooltips.paybackPeriod} />}
              value={metrics.paybackPeriod}
              precision={1}
              suffix=" meses"
            />
          </Card>
        </Col>
      </Row>

      {/* CRESCIMENTO */}
      <Divider orientation="left">📈 Crescimento</Divider>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Novos Cadastros" tooltip={tooltips.newCustomers} />}
              value={metrics.newCustomers}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Novos Ativos" tooltip={tooltips.newActiveCustomers} />}
              value={metrics.newActiveCustomers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Taxa de Ativação" tooltip={tooltips.activationRate} />}
              value={metrics.activationRate}
              precision={2}
              suffix="%"
              prefix={<PercentageOutlined />}
              valueStyle={{ color: metrics.activationRate >= 70 ? '#3f8600' : metrics.activationRate >= 50 ? '#faad14' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Clientes Ativos" tooltip={tooltips.activeCustomers} />}
              value={metrics.activeCustomers}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Crescimento Cadastros" tooltip={tooltips.growthRate} />}
              value={metrics.growthRate}
              precision={2}
              suffix="%"
              prefix={metrics.growthRate >= 0 ? <RiseOutlined /> : <FallOutlined />}
              valueStyle={{ color: metrics.growthRate >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Crescimento Ativos" tooltip={tooltips.activeGrowthRate} />}
              value={metrics.activeGrowthRate}
              precision={2}
              suffix="%"
              prefix={metrics.activeGrowthRate >= 0 ? <RiseOutlined /> : <FallOutlined />}
              valueStyle={{ color: metrics.activeGrowthRate >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      {/* RETENÇÃO E CHURN */}
      <Divider orientation="left">🔄 Retenção e Churn</Divider>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Taxa de Churn" tooltip={tooltips.churnRate} />}
              value={metrics.churnRate}
              precision={2}
              suffix="%"
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Churn MRR" tooltip={tooltips.churnMRR} />}
              value={metrics.churnMRR}
              precision={2}
              prefix={<DollarOutlined />}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Taxa de Retenção" tooltip={tooltips.retentionRate} />}
              value={metrics.retentionRate}
              precision={2}
              suffix="%"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Clientes Reativados" tooltip={tooltips.reactivatedCustomers} />}
              value={metrics.reactivatedCustomers}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* PAGAMENTOS */}
      <Divider orientation="left">💳 Pagamentos</Divider>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Taxa de Inadimplência" tooltip={tooltips.defaultRate} />}
              value={metrics.defaultRate}
              precision={2}
              suffix="%"
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Pagamentos Pendentes" tooltip={tooltips.pendingPayments} />}
              value={metrics.pendingPayments}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Clientes em Dia" tooltip={tooltips.paidCustomers} />}
              value={metrics.paidCustomers}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Valor em Atraso" tooltip={tooltips.overdueAmount} />}
              value={metrics.overdueAmount}
              precision={2}
              prefix={<DollarOutlined />}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      {/* ENGAJAMENTO */}
      <Divider orientation="left">🎮 Engajamento</Divider>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Jogos Criados" tooltip={tooltips.gamesCreated} />}
              value={metrics.gamesCreated}
              prefix={<TrophyOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Jogadores Cadastrados" tooltip={tooltips.playersRegistered} />}
              value={metrics.playersRegistered}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Média Jogadores/Time" tooltip={tooltips.avgPlayersPerTeam} />}
              value={metrics.avgPlayersPerTeam}
              precision={1}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Média Jogos/Time" tooltip={tooltips.avgGamesPerTeam} />}
              value={metrics.avgGamesPerTeam}
              precision={1}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Taxa de Confirmação" tooltip={tooltips.confirmationRate} />}
              value={metrics.confirmationRate}
              precision={2}
              suffix="%"
            />
          </Card>
        </Col>
      </Row>

      {/* CONVOCAÇÕES */}
      <Divider orientation="left">📢 Convocações</Divider>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Total de Convocações" tooltip={tooltips.totalCalls} />}
              value={metrics.totalCalls}
              prefix={<BellOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Convocações/Time" tooltip={tooltips.callsPerTeam} />}
              value={metrics.callsPerTeam}
              precision={1}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Taxa de Resposta" tooltip={tooltips.responseRate} />}
              value={metrics.responseRate}
              precision={2}
              suffix="%"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Horário de Pico" tooltip={tooltips.peakHours} />}
              value={metrics.peakHours}
            />
          </Card>
        </Col>
      </Row>

      {/* COMPARATIVOS */}
      <Divider orientation="left">📊 Indicadores Comparativos</Divider>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Crescimento MoM" tooltip={tooltips.momGrowth} />}
              value={metrics.momGrowth}
              precision={2}
              suffix="%"
              prefix={metrics.momGrowth >= 0 ? <RiseOutlined /> : <FallOutlined />}
              valueStyle={{ color: metrics.momGrowth >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Crescimento WoW" tooltip={tooltips.wowGrowth} />}
              value={metrics.wowGrowth}
              precision={2}
              suffix="%"
              prefix={metrics.wowGrowth >= 0 ? <RiseOutlined /> : <FallOutlined />}
              valueStyle={{ color: metrics.wowGrowth >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Crescimento DoD" tooltip={tooltips.dodGrowth} />}
              value={metrics.dodGrowth}
              precision={2}
              suffix="%"
              prefix={metrics.dodGrowth >= 0 ? <RiseOutlined /> : <FallOutlined />}
              valueStyle={{ color: metrics.dodGrowth >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="MoM Ativos" tooltip={tooltips.momActiveGrowth} />}
              value={metrics.momActiveGrowth}
              precision={2}
              suffix="%"
              prefix={metrics.momActiveGrowth >= 0 ? <RiseOutlined /> : <FallOutlined />}
              valueStyle={{ color: metrics.momActiveGrowth >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="WoW Ativos" tooltip={tooltips.wowActiveGrowth} />}
              value={metrics.wowActiveGrowth}
              precision={2}
              suffix="%"
              prefix={metrics.wowActiveGrowth >= 0 ? <RiseOutlined /> : <FallOutlined />}
              valueStyle={{ color: metrics.wowActiveGrowth >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="DoD Ativos" tooltip={tooltips.dodActiveGrowth} />}
              value={metrics.dodActiveGrowth}
              precision={2}
              suffix="%"
              prefix={metrics.dodActiveGrowth >= 0 ? <RiseOutlined /> : <FallOutlined />}
              valueStyle={{ color: metrics.dodActiveGrowth >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="NRR" tooltip={tooltips.nrr} />}
              value={metrics.nrr}
              precision={2}
              suffix="%"
              valueStyle={{ color: metrics.nrr >= 100 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Quick Ratio" tooltip={tooltips.quickRatio} />}
              value={metrics.quickRatio}
              precision={2}
              suffix="x"
              valueStyle={{ color: metrics.quickRatio >= 4 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Burn Rate" tooltip={tooltips.burnRate} />}
              value={metrics.burnRate}
              precision={2}
              prefix={<DollarOutlined />}
              formatter={(value) => formatCurrency(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Runway" tooltip={tooltips.runway} />}
              value={metrics.runway}
              precision={1}
              suffix=" meses"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<StatTitle text="Health Score" tooltip={tooltips.healthScore} />}
              value={metrics.healthScore}
              precision={1}
              suffix="/100"
              valueStyle={{ 
                color: metrics.healthScore >= 80 ? '#3f8600' : 
                       metrics.healthScore >= 60 ? '#faad14' : '#cf1322' 
              }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Metrics;
